
import CustomizeIcon from '@/components/FileViewer/Icons/CustomizeIcon.vue'
import SearchMediumIcon from './Icons/SearchMediumIcon.vue'
import XIcon from './Icons/XIcon.vue'
import LoadingIcon from './Icons/LoadingIcon.vue'
import ArrowDownCircleIcon from './Icons/ArrowDownCircleIcon.vue'
import ArrowUpCircleIcon from './Icons/ArrowUpCircleIcon.vue'
import TreeTravers from './Helpers/TreeTravers.js'
import ScreenUtils from './Mixins/ScreenUtils.js'
import DispatchEventUtils from './Mixins/DispatchEventUtils.js'

export default {
    name: 'Search',
    components: {
        CustomizeIcon,
        SearchMediumIcon,
        ArrowDownCircleIcon,
        ArrowUpCircleIcon,
        XIcon,
        LoadingIcon
    },
    inject: ['fvAPI_SearchInFile', 'setPage', 'isLoadingPages'],
    mixins: [ScreenUtils, DispatchEventUtils],
    props: {
        isText: Boolean,
        searchFilters: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            searchText: '',
            results: null,
            perPage: 0,
            isSearching: false,
            isNavigating: false,
            selectedResultIndex: 0,
            totalResultsCount: 0
        }
    },
    computed: {
        canSearch () {
            return this.searchText.trim().length > 2
        },
        isLoading () {
            return this.isNavigating || this.isLoadingPages()
        }
    },
    watch: {
        searchText () {
            this.results = null
            this.selectedResultIndex = 0
        }
    },
    methods: {
        startSearch () {
            if (!this.canSearch) {
                return
            }
            this.results = null
            this.selectedResultIndex = 0
            this.isSearching = true
            this.fvAPI_SearchInFile(this.searchText, 1, this.searchFilters)
                .then((result) => {
                    this.results = {}
                    for (let i = 0; i < result.results.length; i++) {
                        this.results[i] = result.results[i]
                    }
                    this.totalResultsCount = result.total - 1
                    this.perPage = result.perPage
                    this.isSearching = false
                    this.goToResult()
                })
                .catch(() => {
                    this.isSearching = false
                })
        },
        loadMoreResults () {
            return new Promise((resolve, reject) => {
                this.isNavigating = true
                const page = ((this.selectedResultIndex - (this.selectedResultIndex % this.perPage)) / this.perPage) + 1
                this.fvAPI_SearchInFile(this.searchText, page, this.searchFilters)
                    .then((result) => {
                        for (let i = 0; i < result.results.length; i++) {
                            this.results[i + ((page - 1) * this.perPage)] = result.results[i]
                        }
                        this.isNavigating = false
                        resolve(true)
                    })
                    .catch((e) => {
                        this.isNavigating = false
                        reject(e)
                    })
            })
        },
        goToResult () {
            this.isNavigating = true
            const result = this.results[this.selectedResultIndex]
            this.setPage(result.pageId)
                .then(() => {
                    let contentContainer = null
                    if (result.type === 'TITLE') {
                        contentContainer = document.querySelector(`#page-${result.pageId}  [data-title-container]`)
                    }
                    if (result.type === 'CONTENT' || result.type === 'PAGE') {
                        contentContainer = document.querySelector(`#page-${result.pageId}  [data-content-container]`)
                    }

                    if (['EXPLANATION', 'REGULATION'].includes(result.type)) {
                        this.dispatch('FileViewer', result.type === 'REGULATION' ? 'show-regulation' : 'show-explanation', {
                            page: { id: result.pageId },
                            idOfTheSelected: result.typeId,
                            show: true
                        })
                        this.$nextTick(() => {
                            if (result.isTitle) {
                                contentContainer = result.type === 'REGULATION'
                                    ? document.querySelector(`#page-${result.pageId} [data-regulation-id="${result.typeId}"] [data-regulation-title-container]`)
                                    : document.querySelector(`#page-${result.pageId} [data-explanation-id="${result.typeId}"] [data-explanation-title-container]`)
                            } else {
                                contentContainer = result.type === 'REGULATION'
                                    ? document.querySelector(`#page-${result.pageId} [data-regulation-id="${result.typeId}"] [data-regulation-content-container]`)
                                    : document.querySelector(`#page-${result.pageId} [data-explanation-id="${result.typeId}"] [data-explanation-content-container]`)
                            }
                            this.setSelection(contentContainer, result)
                        })

                        return
                    }
                    this.setSelection(contentContainer, result)
                })
                .catch((e) => {
                    this.isNavigating = false
                    throw e
                })
        },
        setSelection (contentContainer, result) {
            if (!contentContainer) {
                this.isNavigating = false
                return
            }
            setTimeout(() => {
                const offsets = {
                    startEl: null,
                    startOffset: null,
                    endEl: null,
                    endOffset: null
                }
                TreeTravers.walkInTree((el, startOffset) => {
                    const endOffset = startOffset + el.textContent.length
                    if (!offsets.startEl) {
                        if (result.startOffset < endOffset) {
                            offsets.startEl = el
                            offsets.startOffset = result.startOffset - startOffset
                        }
                    }
                    if (offsets.startEl && result.endOffset <= endOffset) {
                        offsets.endEl = el
                        offsets.endOffset = result.endOffset - startOffset
                        return false
                    }
                }, contentContainer, '#text')
                const selection = document.getSelection()
                selection.removeAllRanges()
                if (this.isText) {
                    selection.setBaseAndExtent(offsets.startEl, offsets.startOffset, offsets.endEl, offsets.endOffset)
                    const currentScrollTop = document.documentElement?.scrollTop
                    const rangTop = Math.round(currentScrollTop + selection.getRangeAt(0)?.getBoundingClientRect().top - Math.round(window.innerHeight / 2))

                    if (Number.isInteger(rangTop)) {
                        document.documentElement.scrollTo({ top: rangTop, behavior: 'instant' })
                    }
                } else {
                    if (offsets.startEl.nodeName === '#text') {
                        offsets.startEl = offsets.startEl.parentElement
                    }
                    if (offsets.endEl.nodeName === '#text') {
                        offsets.endEl = offsets.endEl.parentElement
                    }
                    selection.setBaseAndExtent(offsets.startEl, 0, offsets.endEl, offsets.endEl.childNodes.length)
                    offsets.startEl.scrollIntoView({ block: 'center', behavior: 'instant' })
                }
                this.isNavigating = false
            }, 100)
        },
        nextResult () {
            if (this.selectedResultIndex === this.totalResultsCount) {
                this.selectedResultIndex = 0
            } else {
                this.selectedResultIndex++
            }
            if (this.results && this.selectedResultIndex in this.results) {
                this.goToResult()
                return
            }
            this.loadMoreResults()
                .then(() => {
                    this.goToResult()
                })
        },
        previousResult () {
            if (this.selectedResultIndex === 0) {
                this.selectedResultIndex = this.totalResultsCount
            } else {
                this.selectedResultIndex--
            }
            if (this.results && this.selectedResultIndex in this.results) {
                this.goToResult()
                return
            }
            this.loadMoreResults()
                .then(() => {
                    this.goToResult()
                })
        }
    }
}
